import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SelectField } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { SavedAddressPicker } from '@/components/saved-address-picker';
import { form_error_message } from '@/lib/forms';
import { use_pets } from '@/features/pet/hooks';
import {
  use_preview_training_package,
  use_create_training_package,
  use_pay_training_package,
  use_verify_training_package_payment,
} from '@/features/training-package/hooks';
import type { TrainingPackage, TrainingPackagePreview } from '@/features/training-package/types';
import type { DiscoveryService } from '@/features/discovery/types';
import type { ServicePackage } from '@/features/service-package/types';
import { use_public_service_packages } from '@/features/service-package/hooks';
import { open_razorpay_checkout } from '@/features/booking/razorpay-checkout';
import { use_session_store } from '@/lib/session-store';

const IST = 'Asia/Kolkata';
const today_ist = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });
const DAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

function session_datetime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

type NavState = { service?: DiscoveryService; offer?: ServicePackage; vendor_name?: string; vendor_id?: string };
type Step = 'build' | 'preview' | 'pay' | 'done';

export function TrainingPackagePage() {
  const navigate = useNavigate();
  const { packageId } = useParams<{ packageId: string }>();
  const state = (useLocation().state ?? {}) as NavState;
  const service = state.service;
  // Client change (9 Oct 2026): the customer buys the trainer's package and only picks days, time and start.
  const offers_q = use_public_service_packages(state.vendor_id ?? '', 'TRAINER', { enabled: !state.offer && !!state.vendor_id });
  const offer = state.offer ?? offers_q.data?.find((o) => o.id === packageId);
  const required_days = offer?.sessions_per_week ?? 0;
  const phone = use_session_store((s) => s.phone) ?? undefined;

  const pets_q = use_pets();
  const pets = pets_q.data ?? [];

  const preview_m = use_preview_training_package();
  const create_m = use_create_training_package();
  const pay_m = use_pay_training_package();
  const verify_m = use_verify_training_package_payment();

  const [step, set_step] = useState<Step>('build');
  const [days, set_days] = useState<number[]>([]);
  const [time, set_time] = useState('17:00');
  const [start_date, set_start_date] = useState(today_ist());
  const [pet_id, set_pet_id] = useState('');
  const [saved_address_id, set_saved_address_id] = useState<string | undefined>(undefined);
  const [pickup_address, set_pickup_address] = useState('');
  const [pickup_landmark, set_pickup_landmark] = useState('');
  const [pickup_pincode, set_pickup_pincode] = useState('');
  const [contact_phone, set_contact_phone] = useState('');
  const [access_notes, set_access_notes] = useState('');
  const [error, set_error] = useState<string | null>(null);

  const [preview, set_preview] = useState<TrainingPackagePreview | null>(null);
  const [pkg, set_pkg] = useState<TrainingPackage | null>(null);

  useEffect(() => {
    if (phone && !contact_phone) set_contact_phone(phone);
  }, [phone, contact_phone]);

  const schedule = useMemo(
    () => ({ package_id: packageId ?? '', days_of_week: [...days].sort((a, b) => a - b), time, start_date }),
    [packageId, days, time, start_date],
  );

  // The package promises a fixed count; fewer fitting dates means this schedule can't be bought.
  const short_by = preview ? Math.max(0, preview.required_sessions - preview.total_sessions) : 0;

  function toggle_day(d: number) {
    set_days((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : cur.length >= required_days ? cur : [...cur, d]));
  }

  async function run_preview() {
    set_error(null);
    if (!offer) return;
    if (days.length !== required_days) {
      return set_error(`Pick exactly ${required_days} day${required_days > 1 ? 's' : ''} of the week.`);
    }
    try {
      const p = await preview_m.mutateAsync(schedule);
      if (p.total_sessions === 0) {
        set_error("No sessions match that schedule. Bookings need at least a day's notice, so today never has slots — try starting tomorrow or later, or pick a different time the trainer is open.");
        set_preview(null);
        return;
      }
      set_preview(p);
      set_step('preview');
    } catch (err) {
      set_error(form_error_message(err, 'Could not preview the course.'));
    }
  }

  async function confirm_package() {
    set_error(null);
    if (!pet_id) return set_error('Choose which pet the course is for.');
    if (!pickup_address.trim()) return set_error('Enter a visit address.');
    if (!contact_phone.trim()) return set_error('Enter a contact phone.');
    try {
      const created = await create_m.mutateAsync({
        ...schedule,
        pet_id,
        saved_address_id,
        pickup_address: pickup_address.trim(),
        pickup_landmark: pickup_landmark.trim() || undefined,
        pickup_pincode: pickup_pincode.trim() || undefined,
        contact_phone: contact_phone.trim(),
        access_notes: access_notes.trim() || undefined,
      });
      set_pkg(created);
      set_step('pay');
    } catch (err) {
      set_error(form_error_message(err, 'Could not create the course.'));
    }
  }

  async function pay() {
    if (!pkg) return;
    set_error(null);
    try {
      if (pkg.checkout) {
        await open_razorpay_checkout({
          checkout: pkg.checkout,
          service_name: `${service?.name ?? 'Training'} course`,
          contact: phone,
          on_success: async (resp) => {
            try {
              const verified = await verify_m.mutateAsync({
                id: pkg.id,
                input: {
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                },
              });
              set_pkg(verified);
              set_step('done');
            } catch (err) {
              set_error(form_error_message(err, 'Payment verification failed. If you were charged, contact support.'));
            }
          },
          on_dismiss: () => set_error('Payment cancelled. You can try again.'),
          on_failure: (message) => set_error(message),
        });
      } else {
        const paid = await pay_m.mutateAsync(pkg.id);
        set_pkg(paid);
        set_step('done');
      }
    } catch (err) {
      set_error(form_error_message(err, 'Payment failed. Please try again.'));
    }
  }

  const paying = pay_m.isPending || verify_m.isPending;

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => navigate(state.vendor_id ? `/app/discover/${state.vendor_id}` : '/app/discover')}
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>

      <div>
        <h1 className="font-heading text-2xl font-bold">Buy a training course</h1>
        <p className="text-sm text-muted-foreground">
          {[offer?.name, state.vendor_name, offer ? `${offer.duration_days} days · ${offer.sessions_count} class${offer.sessions_count > 1 ? 'es' : ''} · ₹${offer.price_inr}` : null].filter(Boolean).join(' · ')}
        </p>
      </div>

      {!offer && (
        <Card data-testid="package-no-offer">
          <CardContent className="p-5 text-sm text-muted-foreground">
            {offers_q.isLoading ? 'Loading package…' : 'Open this package from the trainer’s page to book it.'}
          </CardContent>
        </Card>
      )}

      {/* Step 1 — schedule builder */}
      {step === 'build' && offer && (
        <Card>
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-1.5">
              <Label>
                Pick {required_days} day{required_days > 1 ? 's' : ''} of the week ({days.length}/{required_days})
              </Label>
              <div className="flex flex-wrap gap-1.5" data-testid="package-days">
                {DAYS.map((d) => {
                  const active = days.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggle_day(d.value)}
                      data-testid={`package-day-${d.value}`}
                      data-active={active}
                      className={
                        'h-10 w-12 rounded-button border-2 text-sm font-semibold transition-all duration-fast ' +
                        (active
                          ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
                          : 'border-input bg-card text-muted-foreground hover:border-primary/40')
                      }
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="package-time">Time</Label>
                <Input id="package-time" type="time" value={time} onChange={(e) => set_time(e.target.value)} data-testid="package-time" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="package-start">Start date</Label>
                <DatePicker id="package-start" min={today_ist()} value={start_date} onChange={set_start_date} data-testid="package-start" />
              </div>
            </div>

            <p className="rounded-input border-2 border-input p-3 text-sm text-muted-foreground" data-testid="package-offer-summary">
              {offer.sessions_count} class{offer.sessions_count > 1 ? 'es' : ''} within {offer.duration_days} days from your start date ·{' '}
              <span className="font-semibold text-foreground">₹{offer.price_inr}</span> total
            </p>

            {error && <p className="text-sm text-destructive" data-testid="package-error">{error}</p>}

            <Button onClick={run_preview} disabled={preview_m.isPending} variant="gradient" data-testid="package-preview">
              {preview_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Preview sessions
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — preview + pet + visit → confirm */}
      {step === 'preview' && preview && (
        <Card data-testid="package-preview-screen">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-primary" /> {preview.total_sessions} sessions · ₹{preview.total_inr}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 p-5 pt-0">
            {short_by > 0 && (
              <p className="rounded-input border-2 border-destructive p-2 text-xs text-destructive" data-testid="package-short">
                Only {preview.total_sessions} of {preview.required_sessions} fit — the trainer isn't available on some of those dates. Go back and try another time or start date.
              </p>
            )}
            <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border" data-testid="package-session-list">
              {preview.sessions.map((s, i) => (
                <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>{session_datetime(s.slot_start)}</span>
                  <span className="tabular-nums text-muted-foreground">₹{s.price_inr}</span>
                </li>
              ))}
            </ul>

            {pets.length === 0 ? (
              <div className="flex items-center justify-between gap-3 rounded-input border-2 border-warning/30 bg-warning/5 p-3 text-sm" data-testid="package-needs-pet">
                <span>Add your pet before buying a course.</span>
                <Button size="sm" variant="outline" onClick={() => navigate('/app/pets/new')}>Add a pet</Button>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="package-pet">Pet</Label>
                <SelectField
                  id="package-pet"
                  value={pet_id}
                  onValueChange={set_pet_id}
                  data-testid="package-pet"
                  options={[{ value: '', label: 'Select your pet' }, ...pets.map((p) => ({ value: p.id, label: p.name }))]}
                />
              </div>
            )}

            <div className="flex flex-col gap-3 rounded-input border-2 border-brand-outline-variant/15 bg-secondary/30 p-3" data-testid="package-visit-fields">
              <p className="text-sm font-semibold">Visit details</p>
              <SavedAddressPicker
                on_select={(a) => {
                  set_saved_address_id(a.id);
                  set_pickup_address([a.address, a.city].filter(Boolean).join(', '));
                  set_pickup_landmark(a.landmark ?? '');
                  set_pickup_pincode(a.pincode ?? '');
                  if (a.phone) set_contact_phone(a.phone);
                  if (a.access_notes) set_access_notes(a.access_notes);
                }}
                on_clear={() => {
                  set_saved_address_id(undefined);
                  set_pickup_address('');
                  set_pickup_landmark('');
                  set_pickup_pincode('');
                }}
              />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="package-address">Address</Label>
                <Input
                  id="package-address"
                  value={pickup_address}
                  onChange={(e) => {
                    set_saved_address_id(undefined);
                    set_pickup_address(e.target.value);
                  }}
                  placeholder="House / building, street, area"
                  data-testid="package-address"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="package-landmark">Landmark (optional)</Label>
                  <Input id="package-landmark" value={pickup_landmark} onChange={(e) => set_pickup_landmark(e.target.value)} placeholder="Near…" data-testid="package-landmark" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="package-pincode">Pincode (optional)</Label>
                  <Input id="package-pincode" value={pickup_pincode} onChange={(e) => set_pickup_pincode(e.target.value)} placeholder="560001" data-testid="package-pincode" />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="package-contact">Contact phone</Label>
                <Input id="package-contact" value={contact_phone} onChange={(e) => set_contact_phone(e.target.value)} placeholder="+91…" data-testid="package-contact" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="package-access">Access notes (optional)</Label>
                <Input id="package-access" value={access_notes} onChange={(e) => set_access_notes(e.target.value)} placeholder="Gate code, floor, where to meet…" data-testid="package-access" />
              </div>
            </div>

            {error && <p className="text-sm text-destructive" data-testid="package-error">{error}</p>}

            <div className="flex items-center justify-between gap-2">
              <Button variant="ghost" onClick={() => set_step('build')} data-testid="package-back">Edit schedule</Button>
              <Button variant="gradient" onClick={confirm_package} disabled={create_m.isPending || short_by > 0} data-testid="package-confirm">
                {create_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Continue to payment
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — one payment for the whole course */}
      {step === 'pay' && pkg && (
        <Card data-testid="package-payment-screen">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" /> Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 p-5 pt-0">
            <div className="rounded-input border-2 border-brand-outline-variant/15 bg-secondary/40 p-3 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">{pkg.total_sessions}-session course</span>
                <span className="font-semibold tabular-nums">₹{pkg.total_inr}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">From</span>
                <span>{pkg.start_date} · {pkg.duration_days ?? pkg.weeks * 7} days</span>
              </div>
            </div>
            <p className="rounded-input border-2 border-dashed border-brand-outline-variant/30 p-2 text-center text-xs text-muted-foreground">
              {pkg.checkout ? 'Secure payment via Razorpay — one charge for the whole course.' : 'Demo gateway — no real charge. This always succeeds.'}
            </p>

            {error && <p className="text-sm text-destructive" data-testid="package-pay-error">{error}</p>}

            <Button onClick={pay} disabled={paying} variant="gradient" data-testid="package-pay-now">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Pay ₹{pkg.total_inr}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — confirmation */}
      {step === 'done' && pkg && (
        <Card data-testid="package-confirmation">
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-success" />
            <h2 className="font-heading text-xl font-bold">Course booked</h2>
            <p className="text-sm text-muted-foreground">
              Payment captured for all {pkg.total_sessions} sessions. Each session was sent to the trainer — you'll be notified as they confirm.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/app/bookings')} variant="gradient" data-testid="package-go-to-bookings">
                View my training
              </Button>
              <Button onClick={() => navigate('/app/discover')} variant="ghost">Keep browsing</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
